/// <amd-module name="TFS/Admin/Groups/Pivots/Members/Redux/MembersPivotSaga" />
import { effects, SagaIterator } from "redux-saga";
import { ActionsOfType } from "redux/Utils/ActionHelper";
import { GraphClientName, IGraphRestClient } from "TFS/Admin/Common/RestClient/Graph";
import { IPermissionPage, SearchStatus } from "TFS/Admin/Groups/Common/Constants";
import { CustomGraphMember, MembersOperation } from "TFS/Admin/Groups/Common/Contracts";
import * as Resources from "TFS/Admin/Groups/Common/Resources";
import { GroupsComponent, GroupsTelemetryLogger } from "TFS/Admin/Groups/Common/Util/GroupsTelemetryLogger";
import { getProjectName, getUrlForGraphSubject } from "TFS/Admin/Groups/Common/Util/LinkPropsHelper";
import { MembersPivotActions, MembersPivotActionTypes } from "TFS/Admin/Groups/Pivots/Members/Redux/MembersPivotActions";
import { localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { GraphSubject, GraphSubjectQuery, PagedGraphMembers } from "VSS/Platform/Contracts/Graph";
import { IVssLocationService } from "VSS/Platform/Location";
import { MessageCardSeverity } from "VSSUI/MessageCard";

/**
 * Root work item saga.
 */
export function* membersPivotSaga(): SagaIterator {
    // Register the initialize Members handler
    yield effects.takeEvery(MembersPivotActionTypes.InitView, initializeMembersPivot);
    yield effects.takeEvery(MembersPivotActionTypes.OnRenderMissingItem, fetchMoreMembers);
    yield effects.takeLatest(MembersPivotActionTypes.SearchMembers, searchMembers);
}

export function* initializeMembersPivot(action: ActionsOfType<MembersPivotActions, MembersPivotActionTypes.InitView>): SagaIterator {
    const { scopeDescriptor, permissionPage } = action.payload;
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const locationService: IVssLocationService = (yield effects.call([pageContext, pageContext.getService], "IVssLocationService")) as any;

    const telemetryLogger: GroupsTelemetryLogger = new GroupsTelemetryLogger(pageContext, GroupsComponent.MembersPivot, "Initialize Members pivot");
    yield effects.call(telemetryLogger.captureAttemptStart);

    const graphClient: IGraphRestClient = (yield effects.call([pageContext, pageContext.getRestClient], GraphClientName)) as any;

    try {
        const pagedMembers: PagedGraphMembers = (yield effects.call(
            [graphClient, graphClient.listMembers],
            undefined,
            undefined,
            undefined,
            undefined,
            scopeDescriptor
        )) as any;
        let members: CustomGraphMember[] = [];

        if (pagedMembers) {
            const projectName =
                permissionPage != IPermissionPage.Organization ? yield effects.call(getProjectName, pageContext, telemetryLogger) : undefined;

            yield effects.all(
                pagedMembers.graphMembers.map((pagedMember) =>
                    effects.call(function* () {
                        const url = (yield effects.call(
                            getUrlForGraphSubject,
                            pagedMember.descriptor,
                            permissionPage,
                            locationService,
                            telemetryLogger,
                            projectName
                        )) as any;
                        members.push({ ...(pagedMember as CustomGraphMember), linkProps: { href: url } });
                    })
                )
            );

            yield effects.put(MembersPivotActions.initialMembersLoadSucceeded(members, pagedMembers.continuationToken));
        } else {
            yield effects.put(
                MembersPivotActions.memberOperationStatus(MembersOperation.Fetch, Resources.FetchMembersFailed, MessageCardSeverity.Error)
            );
            yield effects.put(MembersPivotActions.membersLoadFailed());
        }
    } catch (error) {
        yield effects.call(telemetryLogger.captureError, error.message || error);
        yield effects.put(MembersPivotActions.memberOperationStatus(MembersOperation.Fetch, Resources.FetchMembersFailed, MessageCardSeverity.Error));
        yield effects.put(MembersPivotActions.membersLoadFailed());
    }
}

export function* fetchMoreMembers(action: ActionsOfType<MembersPivotActions, MembersPivotActionTypes.OnRenderMissingItem>): SagaIterator {
    const { continuationToken, scopeDescriptor, permissionPage } = action.payload;
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const locationService: IVssLocationService = (yield effects.call([pageContext, pageContext.getService], "IVssLocationService")) as any;

    const telemetryLogger: GroupsTelemetryLogger = new GroupsTelemetryLogger(pageContext, GroupsComponent.MembersPivot, "Fetch more members");
    yield effects.call(telemetryLogger.captureAttemptStart);
    const graphClient: IGraphRestClient = (yield effects.call([pageContext, pageContext.getRestClient], GraphClientName)) as any;

    if (continuationToken && continuationToken.length) {
        try {
            let pagedMembers: PagedGraphMembers = (yield effects.call(
                [graphClient, graphClient.listMembers],
                continuationToken.toString(),
                undefined,
                undefined,
                undefined,
                scopeDescriptor
            )) as any;
            let members: CustomGraphMember[] = [];

            if (pagedMembers) {
                const projectName =
                    permissionPage != IPermissionPage.Organization ? yield effects.call(getProjectName, pageContext, telemetryLogger) : undefined;

                yield effects.all(
                    pagedMembers.graphMembers.map((pagedMember) =>
                        effects.call(function* () {
                            const url = (yield effects.call(
                                getUrlForGraphSubject,
                                pagedMember.descriptor,
                                permissionPage,
                                locationService,
                                telemetryLogger,
                                projectName
                            )) as any;
                            members.push({ ...(pagedMember as CustomGraphMember), linkProps: { href: url } });
                        })
                    )
                );

                yield effects.put(MembersPivotActions.fetchMoreMembersSucceeded(members, pagedMembers.continuationToken));
            } else {
                yield effects.put(MembersPivotActions.membersLoadFailed());
                yield effects.put(
                    MembersPivotActions.memberOperationStatus(MembersOperation.Fetch, Resources.FetchMembersFailed, MessageCardSeverity.Error)
                );
            }
        } catch (error) {
            yield effects.call(telemetryLogger.captureError, error.message || error);
            yield effects.put(
                MembersPivotActions.memberOperationStatus(MembersOperation.Fetch, Resources.FetchMembersFailed, MessageCardSeverity.Error)
            );
            yield effects.put(MembersPivotActions.membersLoadFailed());
        }
    } else {
        yield effects.put(MembersPivotActions.memberOperationStatus(MembersOperation.Fetch, Resources.FetchMembersFailed, MessageCardSeverity.Error));
    }
}

export function* searchMembers(action: ActionsOfType<MembersPivotActions, MembersPivotActionTypes.SearchMembers>): SagaIterator {
    const { newKeyword, oldKeyword, scopeDescriptor, isExpandSearchToOrg, permissionPage } = action.payload;
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;

    const telemetryLogger: GroupsTelemetryLogger = new GroupsTelemetryLogger(pageContext, GroupsComponent.MembersPivot, "Search Members");
    yield effects.call(telemetryLogger.captureAttemptStart);

    if (!newKeyword) {
        // Reset view
        yield effects.put(MembersPivotActions.updateSearchStatus(SearchStatus.None));
    } else if (oldKeyword !== undefined && localeIgnoreCaseComparer(newKeyword, oldKeyword) !== 0) {
        // Search

        try {
            const locationService: IVssLocationService = (yield effects.call([pageContext, pageContext.getService], "IVssLocationService")) as any;
            const graphClient: IGraphRestClient = (yield effects.call([pageContext, pageContext.getRestClient], GraphClientName)) as any;

            const graphSubjectQuery: GraphSubjectQuery = {
                query: newKeyword,
                scopeDescriptor: scopeDescriptor,
                subjectKind: ["User", "ServicePrincipal"]
            };

            // Search
            const graphSubjects: GraphSubject[] = (yield effects.call([graphClient, graphClient.querySubjects], graphSubjectQuery)) as any;

            const resultMembers: CustomGraphMember[] = [];

            if (graphSubjects && graphSubjects.length > 0) {
                const projectName =
                    permissionPage != IPermissionPage.Organization ? yield effects.call(getProjectName, pageContext, telemetryLogger) : undefined;

                yield effects.all(
                    graphSubjects.map((graphSubject) =>
                        effects.call(function* () {
                            const url = (yield effects.call(
                                getUrlForGraphSubject,
                                graphSubject.descriptor,
                                permissionPage,
                                locationService,
                                telemetryLogger,
                                projectName
                            )) as any;
                            resultMembers.push({ ...(graphSubject as CustomGraphMember), linkProps: { href: url } });
                        })
                    )
                );
            }

            yield effects.put(MembersPivotActions.updateSearchResult(newKeyword, resultMembers, false));
        } catch (error) {
            yield effects.put(MembersPivotActions.updateSearchStatus(SearchStatus.None)); // Reset view

            yield effects.call(telemetryLogger.captureError, error.message || error);
        }
    } else {
        // Search did not change
        yield effects.put(MembersPivotActions.updateSearchStatus(SearchStatus.Completed));
    }
}
